import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// SplitText is Club GSAP. If your build doesn’t include it, this import may fail.
// Keep it if you truly have it installed/available; otherwise remove the import
// and the code will warn/skip.
import { SplitText } from "gsap/SplitText";

import { Power2 } from "gsap/gsap-core";

gsap.registerPlugin(ScrollTrigger);
// Register SplitText only if it exists
if (SplitText) gsap.registerPlugin(SplitText);

function $(selector, root = document) {
    return root.querySelector(selector);
}
function $all(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
}

function hasAny(selector) {
    return document.querySelector(selector) !== null;
}

function initHeadingAnimation() {
    if (!SplitText) {
        // If SplitText is not available in production, prevent hard crash
        console.warn("GSAP SplitText is not available. Text animations skipped.");
        return;
    }

    // ---- .text-effect ----
    const textHeading = $all(".text-effect");
    if (textHeading.length) {
        textHeading.forEach((el) => {
            // Re-init safety (optional)
            if (el.anim) {
                el.anim.progress(1).kill();
            }
            if (el.split?.revert) {
                el.split.revert();
            }

            el.split = new SplitText(el, {
                type: "lines,words,chars",
                linesClass: "split-line",
            });

            // your original check is redundant because selector is .text-effect
            gsap.set(el.split.chars, { opacity: 0.3, x: -7 });

            el.anim = gsap.to(el.split.chars, {
                scrollTrigger: {
                    trigger: el,
                    start: "top 92%",
                    end: "top 60%",
                    markers: false,
                    scrub: 1,
                },
                x: 0,
                y: 0,
                opacity: 1,
                duration: 0.7,
                stagger: 0.2,
            });
        });
    }

    // ---- .text-anime-style-1 ----
    const style1 = $all(".text-anime-style-1");
    if (style1.length) {
        const staggerAmount = 0.05;
        const delayValue = 0.5;

        style1.forEach((element) => {
            const split = new SplitText(element, { type: "chars, words" });
            gsap.from(split.words, {
                duration: 1,
                delay: delayValue,
                x: 20,
                autoAlpha: 0,
                stagger: staggerAmount,
                scrollTrigger: { trigger: element, start: "top 85%" },
            });
        });
    }

    // ---- .text-anime-style-2 ----
    const style2 = $all(".text-anime-style-2");
    if (style2.length) {
        const staggerAmount = 0.03;
        const translateXValue = 20;
        const delayValue = 0.1;
        const easeType = "power2.out";

        style2.forEach((element) => {
            const split = new SplitText(element, { type: "chars, words" });
            gsap.from(split.chars, {
                duration: 1,
                delay: delayValue,
                x: translateXValue,
                autoAlpha: 0,
                stagger: staggerAmount,
                ease: easeType,
                scrollTrigger: { trigger: element, start: "top 85%" },
            });
        });
    }

    // ---- .text-anime-style-3 ----
    const style3 = $all(".text-anime-style-3");
    if (style3.length) {
        style3.forEach((element) => {
            if (element.animation) {
                element.animation.progress(1).kill();
                element.split?.revert?.();
            }

            element.split = new SplitText(element, {
                type: "lines,words,chars",
                linesClass: "split-line",
            });

            gsap.set(element, { perspective: 400 });
            gsap.set(element.split.chars, { opacity: 0, x: 50 });

            element.animation = gsap.to(element.split.chars, {
                scrollTrigger: { trigger: element, start: "top 90%" },
                x: 0,
                opacity: 1,
                duration: 1,
                ease: "back.out(1.7)",
                stagger: 0.02,
            });
        });
    }
}

// Run after fonts are ready (if supported)
function boot() {
    initHeadingAnimation();

    // ---- Image Reveal Animation (.reveal) ----
    const revealContainers = $all(".reveal");
    if (revealContainers.length) {
        revealContainers.forEach((container) => {
            const image = container.querySelector("img");
            if (!image) return;

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: container,
                    toggleActions: "play none none none",
                },
            });

            tl.set(container, { autoAlpha: 1 });
            tl.from(container, {
                duration: 1,
                xPercent: -100,
                ease: Power2.out,
            });
            tl.from(image, {
                duration: 1,
                xPercent: 100,
                scale: 1,
                delay: -1,
                ease: Power2.out,
            });
        });
    }
}

// Only boot if something relevant exists (like your original)
if (hasAny(".text-effect") || hasAny(".text-anime-style-1") || hasAny(".text-anime-style-2") || hasAny(".text-anime-style-3") || hasAny(".reveal")) {
    if (document.fonts?.ready) {
        document.fonts.ready.then(boot);
    } else {
        window.addEventListener("load", boot);
    }
}
